import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';

// Actions
import {
  clearPortfolio,
  setComparisonItemName,
  setPortfolioDiscretionaryMer,
  setPortfolioName,
  setPortfolioSize,
  togglePortfolioValueLocked,
  setShowTaxDetails,
  removeTaxDetailsFromPortfolio,
} from '@/redux/modules/edit-portfolio';

// Components
import { ConfirmUnlockPortfolioValueModal } from '../ConfirmUnlockPortfolioValueModal/ConfirmUnlockPortfolioValueModal';
import PortfolioEditorPlanAccountPicker from './components/PortfolioEditorPlanAccountPicker';
import PortfolioEditorQuickSearch from './components/PortfolioEditorQuickSearch/PortfolioEditorQuickSearch';
import PortfolioEditorRiskProfilePicker from './components/PortfolioEditorRiskProfilePicker/PortfolioEditorRiskProfilePicker';
import { EditableInput, ToggleSwitch, UnitInput } from '@/views/components/inputs';
import { IconButton } from '@/views/components/buttons';
import { withErrorBoundary } from '@/views/components/errors';

// Types
import ComparisonItem from '@/types/comparison-item';
import Locale from '@/types/locale';
import Nullable from '@/types/nullable';
import Portfolio from '@/types/portfolio';
import User from '@/types/user';
import { EditablePortfolioFields } from '@/views/components/types/portfolios';
import { ReduxState } from '@/redux/modules/types';
import { UnitSymbolSection } from '../../../../inputs/UnitInput';

// Utils
import { DEFAULT_DECIMALS, formatLocalizedString, formatMessage, FormattedMessage, formatNumber } from '@/utils/i18n';
import { canViewPlanning, isCanViewRiskProfile } from '@/utils/portfolio/portfolio';
import { getPortfolioMissingOrInvalidHoldings } from '@/utils/portfolio/utils';
import { PERMISSIONS, userHasPermission } from '@/utils/permissions';
import { selectShelfStatusedPortfolioHoldings } from '@/redux/modules/shelf-management/selectors';
import { canAccessTaxAnalysis } from '@/utils/feature-access';
import WarningModal from '../../../WarningModal';

enum PORTFOLIO_EDITOR_FIELDS {
  ADVISORY_FEE = 'advisoryFee',
  TOTAL_VALUE = 'totalValue',
}

type DispatchProps = {
  clearPortfolio: () => void;
  setComparisonItemName: (name: string) => void;
  setPortfolioDiscretionaryMer: (value: Nullable<number>) => void;
  setPortfolioName: (name: string) => void;
  setPortfolioSize: (value: Nullable<number>, preserveHoldingDollars?: boolean) => void;
  togglePortfolioValueLocked: () => void;
  setShowTaxDetails: (show: boolean) => Promise<void>;
  removeTaxDetailsFromPortfolio: () => void;
};

type StateProps = {
  comparisonItems: ComparisonItem[];
  isPortfolioValueLocked?: boolean;
  locale?: Locale;
  portfolio: Portfolio;
  portfolioHasMissingHoldings: boolean;
  isCreatingModel?: boolean;
  user?: Nullable<User>;
  showTaxDetails?: boolean;
};

type Props = DispatchProps &
  StateProps & {
    isSaving: boolean;
    onChangePlanAccount: () => void;
  };

const PortfolioEditorOverview = ({
  comparisonItems,
  isPortfolioValueLocked,
  isSaving,
  locale,
  portfolio,
  portfolioHasMissingHoldings,
  isCreatingModel,
  user,
  clearPortfolio,
  onChangePlanAccount,
  setComparisonItemName,
  setPortfolioDiscretionaryMer,
  setPortfolioName,
  setPortfolioSize,
  togglePortfolioValueLocked,
  showTaxDetails,
  setShowTaxDetails,
  removeTaxDetailsFromPortfolio,
}: Props) => {
  const comparisonItem = comparisonItems.find(item => item._id === portfolio._id);
  const DEFAULT_OPTIONS = {
    editableFields: {
      holdings: false,
      mer: false,
      name: false,
      size: false,
    },
    totalValue: 100000,
  };

  const [editableFields, setEditableFields] = useState<EditablePortfolioFields>(DEFAULT_OPTIONS.editableFields);
  const [totalValueIsFocused, setTotalValueIsFocused] = useState<boolean>(false);
  const [showConfirmUnlockModal, setShowConfirmUnlockModal] = useState<boolean>(false);
  const [showTaxDetailsWarning, setShowTaxDetailsWarning] = useState(false);
  const isTaxAnalysisFeatureEnabled = canAccessTaxAnalysis(user, comparisonItem?.portfolioType);
  const isTaxDetailsEnabled = isTaxAnalysisFeatureEnabled && !isCreatingModel;

  const initEditableFields = () => {
    const fields = {
      holdings: true,
      mer: true,
      name: true,
      size: true,
    };

    if (!comparisonItem?.enterprise && !comparisonItem?.providedBy?.id && !!comparisonItem?.overrideHoldings?.length) {
      return {
        ...fields,
        holdings: false,
      };
    }

    return fields;
  };

  useEffect(() => {
    setEditableFields(initEditableFields());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolio]);

  const formatTotalValue = (capital: number) => (totalValueIsFocused ? capital : formatNumber(capital, DEFAULT_DECIMALS, locale));

  const onFocusTotalValue = () => {
    setTotalValueIsFocused(true);
  };

  const onBlurTotalValue = () => {
    const { size } = portfolio;

    if (!size) {
      clearPortfolio();
    }

    setTotalValueIsFocused(false);
  };

  const onFocusAdvisoryFee = () => {
    if (!portfolio.discretionaryMer) {
      setPortfolioDiscretionaryMer(null);
    }
  };

  const onBlurAdvisoryFee = () => {
    const isStillEmpty = portfolio.discretionaryMer === null;
    if (isStillEmpty) {
      setPortfolioDiscretionaryMer(0);
    }
  };

  const getAdvisorFee = (): Nullable<number> => {
    // First load we have undefined but we want to show 0
    if (portfolio.discretionaryMer === undefined) return 0;

    return portfolio.discretionaryMer === null ? null : Number((portfolio.discretionaryMer * 100).toFixed(4));
  };

  const updateComparisonItemName = () => {
    if (comparisonItem) {
      setComparisonItemName(formatLocalizedString(portfolio.name));
    }
  };

  const onValueFieldChange = (value: string, type: PORTFOLIO_EDITOR_FIELDS) => {
    let newValue: Nullable<number> = null;

    if (value.trim()) {
      newValue = Number(value);
    }

    if (type === PORTFOLIO_EDITOR_FIELDS.TOTAL_VALUE) {
      const roundedValue = newValue ? +newValue.toFixed(2) : null;
      setPortfolioSize(roundedValue);
    }

    if (type === PORTFOLIO_EDITOR_FIELDS.ADVISORY_FEE) {
      const formattedValue = newValue !== null ? newValue * 0.01 : null;
      setPortfolioDiscretionaryMer(formattedValue);
    }
  };

  const lockTooltipElement = () => {
    if (!userHasPermission(user, PERMISSIONS.PORTFOLIO_EDITOR_LOCK_TOTAL_VALUE)) {
      return undefined;
    }

    const isLocked = isPortfolioValueLocked;
    const tooltipMessage = isLocked
      ? formatMessage('PortfolioEditorBuilder.Overview.LockedValueTooltip')
      : formatMessage('PortfolioEditorBuilder.Overview.UnlockedValueTooltip');
    const icon = isLocked ? 'ds-lock' : 'ds-lock-open';

    const onClickHandler = (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      if (isLocked) {
        setShowConfirmUnlockModal(true);
      } else {
        togglePortfolioValueLocked();
      }
    };

    return (
      <div className="lock-portfolio-value">
        <IconButton icon={icon} tooltip={tooltipMessage} className="icon-lock-portfolio-value" tooltipClassName="custom-tooltip-v2" onClick={onClickHandler} />
      </div>
    );
  };

  const handleConfirmUnlock = () => {
    const totalHoldingsValue = portfolio.holdings.reduce((sum, holding) => sum + (holding.dollars || 0), 0);

    setPortfolioSize(totalHoldingsValue, true);

    togglePortfolioValueLocked();
    setShowConfirmUnlockModal(false);
  };

  const handleCancelUnlock = () => {
    setShowConfirmUnlockModal(false);
  };

  const toggleTaxDetails = () => {
    if (showTaxDetails) {
      setShowTaxDetailsWarning(true);
    } else {
      setShowTaxDetails(true);
    }
  };

  const handleConfirmTaxDetailsOff = () => {
    removeTaxDetailsFromPortfolio();
    setShowTaxDetails(false);
    setShowTaxDetailsWarning(false);
  };

  const handleCancelTaxDetailsOff = () => {
    setShowTaxDetailsWarning(false);
  };

  return (
    <div className="portfolio-editor-overview">
      <div className="portfolio-editor-overview-info">
        <EditableInput
          className="editable-input-v2 fixed-width-input"
          data-private
          data-dd-privacy="mask"
          defaultToEdit={!portfolio.name}
          onBlur={updateComparisonItemName}
          onValueChange={setPortfolioName}
          placeholder={formatMessage('PortfolioEditorBuilder.Overview.NamePlaceholder')}
          readOnly={!editableFields?.name || portfolioHasMissingHoldings}
          value={formatLocalizedString(portfolio.name)}
        />
        <div className="portfolio-editor-overview-search">
          <PortfolioEditorQuickSearch portfolioHasMissingHoldings={portfolioHasMissingHoldings} />
        </div>
      </div>

      <div className="portfolio-editor-overview-values">
        <div className="value-and-fee-container">
          <div className="value">
            <label className="label-v2" htmlFor="total-value">
              <FormattedMessage id="PortfolioEditorBuilder.Overview.TotalValue" />
            </label>

            {editableFields?.size && (
              <UnitInput
                className="unit-input-v2"
                controlled={false}
                disabled={isSaving || portfolioHasMissingHoldings || showTaxDetails}
                id="total-value"
                unitSymbol="$"
                symbolSection={UnitSymbolSection.LEFT}
                rightSection={lockTooltipElement()}
                min={0}
                name="total-value"
                onBlur={onBlurTotalValue}
                onFocus={onFocusTotalValue}
                onValueChange={value => onValueFieldChange(value, PORTFOLIO_EDITOR_FIELDS.TOTAL_VALUE)}
                type={totalValueIsFocused ? 'number' : 'text'}
                value={formatTotalValue(portfolio.size !== undefined ? portfolio.size : DEFAULT_OPTIONS.totalValue)}
              />
            )}
          </div>
          <div className="fee">
            <label className="label-v2" htmlFor="advisory-fee">
              <FormattedMessage id="PortfolioEditorBuilder.Overview.AdvisoryFee" />
            </label>

            {editableFields?.mer && (
              <UnitInput
                controlled={false}
                className="unit-input-v2"
                disabled={isSaving || portfolioHasMissingHoldings}
                id="advisory-fee"
                name="advisory-fee"
                onValueChange={value => onValueFieldChange(value, PORTFOLIO_EDITOR_FIELDS.ADVISORY_FEE)}
                onFocus={onFocusAdvisoryFee}
                onBlur={onBlurAdvisoryFee}
                type="number"
                unitSymbol={formatMessage('HoldingsEditor.DiscretionaryMERUnit')}
                value={getAdvisorFee()}
              />
            )}
          </div>
        </div>
        <div className="plan-and-risk-container">
          {isTaxDetailsEnabled && (
            <div className="portfolio-editor-tax-details-toggle">
              <ToggleSwitch
                toggleTooltipLabel={formatMessage('PortfolioEditorBuilder.Overview.TaxDetailsTooltip')}
                checked={showTaxDetails}
                onChange={toggleTaxDetails}
              >
                <span>{formatMessage('PortfolioEditorBuilder.Overview.TaxDetails')}</span>
              </ToggleSwitch>
            </div>
          )}
          {canViewPlanning(user, portfolio?.contact) && (
            <PortfolioEditorPlanAccountPicker
              contact={portfolio?.contact}
              planAccountId={portfolio.planning?.planAccountId}
              onChangePlanAccount={onChangePlanAccount}
            />
          )}
          {isCanViewRiskProfile(user) && <PortfolioEditorRiskProfilePicker portfolioHasMissingHoldings={portfolioHasMissingHoldings} />}
        </div>
      </div>

      <ConfirmUnlockPortfolioValueModal show={showConfirmUnlockModal} onConfirm={handleConfirmUnlock} onCancel={handleCancelUnlock} />
      <WarningModal
        className="portfolio-editor-tax-details-warning-modal"
        show={showTaxDetailsWarning}
        onConfirm={handleConfirmTaxDetailsOff}
        onCancel={handleCancelTaxDetailsOff}
        title={formatMessage('PortfolioEditorBuilder.Overview.TaxDetailsWarningModal.Title')}
        body={formatMessage('PortfolioEditorBuilder.Overview.TaxDetailsWarningModal.Body')}
      />
    </div>
  );
};

const mapStateToProps = (state: ReduxState): StateProps => {
  const missingHoldings = getPortfolioMissingOrInvalidHoldings(selectShelfStatusedPortfolioHoldings(state.editPortfolio.portfolio)) || [];

  return {
    comparisonItems: state.comparison.activeComparison?.items || [],
    isPortfolioValueLocked: state.editPortfolio.isPortfolioValueLocked,
    locale: state.intl.locale,
    portfolio: state.editPortfolio.portfolio,
    portfolioHasMissingHoldings: !!missingHoldings.length,
    user: state.auth.user,
    showTaxDetails: state.editPortfolio.showTaxDetails,
    isCreatingModel: state.editPortfolio.isCreatingModel,
  };
};

export default connect(mapStateToProps, {
  setPortfolioName,
  setComparisonItemName,
  setPortfolioSize,
  clearPortfolio,
  setPortfolioDiscretionaryMer,
  togglePortfolioValueLocked,
  setShowTaxDetails,
  removeTaxDetailsFromPortfolio,
})(withErrorBoundary(PortfolioEditorOverview));
